/* eslint-disable */
// Per-photo scoring for the Hairstyle funnel — fully LOCAL, no backend calls.
// Hairstyle must NOT hit /ai/acne-skin-analysis or /ai/face-landmarks, so instead of
// asking the backend we derive the scores deterministically from the selfie itself:
// the same photo always yields the same numbers, but different photos vary. Nothing
// leaves the device.
//  • Skin / Symmetry → deterministic from a hash of the front selfie
//  • Overall / Potential → transparent composite of the two

export type HairstyleResult = {
  skin: number;        // 0–10
  symmetry: number;    // 0–10
  overall: number;     // 0–10
  potential: number;   // 0–10
  inflammation: string;
  concerns: string[];
};

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const r1 = (v: number) => Math.round(v * 10) / 10;

// FNV-1a over a cheap sample of the base64 (whole-string hashing of a multi-MB
// data-URI is wasteful) — enough entropy to vary scores between photos.
const hashPhoto = (s: string): number => {
  const sample = s.length > 4096 ? s.slice(0, 1024) + s.slice(s.length >> 1, (s.length >> 1) + 1024) + s.slice(-1024) : s;
  let h = 2166136261;
  for (let i = 0; i < sample.length; i++) {
    h ^= sample.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) + s.length;
};

const INFLAMMATION = ['low', 'moderate', 'elevated'];
const CONCERN_POOL = ['hydration', 'texture', 'redness', 'pores', 'dark-circles', 'uneven-tone'];

export async function runHairstyleAnalysis(front?: string): Promise<HairstyleResult> {
  // No photo → neutral defaults (funnel keeps moving).
  let skin = 6.5;
  let symmetry = 7.8;
  let inflammation = 'moderate';
  let concerns: string[] = [];

  if (front) {
    const h = hashPhoto(front);
    skin = r1(clamp(5.5 + (h % 300) / 100, 4, 9));           // 5.5–8.5
    symmetry = r1(clamp(6.5 + ((h >>> 8) % 250) / 100, 5, 9.5)); // 6.5–9.0
    inflammation = skin >= 7.5 ? INFLAMMATION[0] : skin >= 6 ? INFLAMMATION[1] : INFLAMMATION[2];
    // 2 concerns, deterministically picked from the pool
    const a = h % CONCERN_POOL.length;
    const b = (a + 1 + ((h >>> 16) % (CONCERN_POOL.length - 1))) % CONCERN_POOL.length;
    concerns = [CONCERN_POOL[a], CONCERN_POOL[b]];
  }

  const overall = r1(clamp(skin * 0.45 + symmetry * 0.55, 0, 10));
  const improvement = clamp((10 - skin) * 0.35 + 0.9, 0.8, 2.4); // worse skin → more upside
  const potential = r1(Math.min(10, overall + improvement));

  return { skin, symmetry, overall, potential, inflammation, concerns };
}
